import { useState, useEffect } from "react";
import { X, Save, Edit3 } from "lucide-react";
import type { GuardrailItemDetailResponse } from "../../gen/models";

interface EditGuardrailModalProps {
  isOpen: boolean;
  item: GuardrailItemDetailResponse | null;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    category: string;
    action: string;
    severity: string;
    default_threshold: number;
    status: string;
    metadata: Record<string, any>;
  }) => Promise<void>;
  submitting: boolean;
}

const GUARDRAIL_CATEGORIES = [
  { value: "prompt_injection", label: "Prompt Injection" },
  { value: "jailbreak", label: "Jailbreak & System Override" },
  { value: "off_topic", label: "Off-Topic Inquiry" },
  { value: "abusive", label: "Abusive / Harassment" },
  { value: "brand_safety", label: "Brand Safety & Competitors" },
  { value: "pii_leakage", label: "PII & Credentials Leakage" },
  { value: "boundary", label: "General Boundary" },
];

export function EditGuardrailModal({
  isOpen,
  item,
  onClose,
  onSubmit,
  submitting,
}: EditGuardrailModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("prompt_injection");
  const [action, setAction] = useState<string>("block");
  const [severity, setSeverity] = useState<string>("critical");
  const [threshold, setThreshold] = useState<number>(0.82);
  const [status, setStatus] = useState<string>("active");
  const [onBlockAction, setOnBlockAction] = useState<string>("silent");
  const [cannedText, setCannedText] = useState("");
  const [advisory, setAdvisory] = useState("");

  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setDescription(item.description || "");
      setCategory(item.category);
      setAction(item.action);
      setSeverity(item.severity);
      setThreshold(item.default_threshold);
      setStatus(item.status);
      const meta = item.metadata || {};
      setOnBlockAction((meta as any).on_block_action || "silent");
      setCannedText((meta as any).canned_response_text || "");
      setAdvisory((meta as any).advisory || "");
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const existingMeta = (item.metadata || {}) as Record<string, any>;
    const updatedMeta = {
      ...existingMeta,
      on_block_action: onBlockAction,
      canned_response_text: cannedText.trim(),
      advisory: advisory.trim(),
    };

    await onSubmit({
      title: title.trim(),
      description: description.trim(),
      category,
      action,
      severity,
      default_threshold: Number(threshold),
      status,
      metadata: updatedMeta,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 dark:bg-black/70 backdrop-blur-xs">
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="p-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-stone-600 dark:text-stone-300" />
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              Edit Guardrail Policy
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
              Policy Title *
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              >
                {GUARDRAIL_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                Lifecycle Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              >
                <option value="active">Active (Enforcing)</option>
                <option value="draft">Draft (Inactive)</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                Action
              </label>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 font-semibold focus:border-stone-500 focus:outline-hidden"
              >
                <option value="block">Block</option>
                <option value="flag">Flag</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
                Threshold
              </label>
              <input
                type="number"
                step="0.01"
                min="0.0"
                max="1.0"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 font-mono focus:border-stone-500 focus:outline-hidden"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">
              Description
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
            />
          </div>

          {action === "block" ? (
            <div className="p-3 rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/30 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-rose-900 dark:text-rose-200">
                  On-Block Behavior
                </span>
                <select
                  value={onBlockAction}
                  onChange={(e) => setOnBlockAction(e.target.value)}
                  className="rounded-md border border-rose-300 dark:border-rose-800 bg-white dark:bg-stone-900 px-2 py-1 text-xs text-stone-900 dark:text-stone-100"
                >
                  <option value="silent">Silent Drop</option>
                  <option value="canned_response">Canned Response</option>
                </select>
              </div>

              {onBlockAction === "canned_response" && (
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-rose-900 dark:text-rose-300 block">
                    Canned Response Text
                  </label>
                  <input
                    type="text"
                    value={cannedText}
                    onChange={(e) => setCannedText(e.target.value)}
                    className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/30 space-y-2">
              <label className="text-xs font-semibold text-amber-900 dark:text-amber-200 block">
                Advisory Guidance Prompt
              </label>
              <textarea
                rows={2}
                value={advisory}
                onChange={(e) => setAdvisory(e.target.value)}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-stone-200 dark:border-stone-800">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-xs disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{submitting ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
